import { Drawer, Tooltip, Modal } from "flowbite-react";
import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SuccessModal from "./SuccessModal";
import FailureModal from "./FailureModal";
import Loader from "./Loader";
import moment from "moment";
import {
  clog,
  getCurrentWorkspacePlanId,
  getPlanDetails,
  isUserOwnerOfWorkspace,
} from "../lib/utils";
import UpgradeModal from "./UpgradeModal";
import Lottie from "lottie-react";
import * as XLSX from "xlsx";
import DetailsDrawer from "./DetailsDrawer";

// Import animation
import noLeadsAnimation from "../assets/animations/downloaded-animation.json";
import { toast } from "react-toastify";

const MainContent = ({
  activeTab,
  setActiveTab,
  filters,
  onFilterInfoChange,
}) => {
  const [openSuccessModal, setopenSuccessModal] = useState(undefined);
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");
  const removeModal = () => {
    setopenSuccessModal(undefined);
    setopenFailureModal(undefined);
  };
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Leads state and loading
  const [allLeads, setAllLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // API pagination info
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalPagesAPI, setTotalPagesAPI] = useState(0);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isLead, setIsLead] = useState(true); // Track if there are leads tracked yet

  // Rating (thumbs up/down) state
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingLeadId, setRatingLeadId] = useState(null);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  // React Router navigate hook
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to format milliseconds into HH:MM:SS
  // const formatDuration = (pageVisits) => {
  //   let milliseconds = 0;
  //   milliseconds = pageVisits?.reduce((a, b) => a + b?.totalTime, 0) || 0;
  //   const totalSeconds = Math.floor(milliseconds / 1000);
  //   const h = Math.floor(totalSeconds / 3600);
  //   const m = Math.floor((totalSeconds % 3600) / 60);
  //   const s = totalSeconds % 60;
  //   return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
  // };
  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
  };

  const abortControllerRef = useRef(null);

  // Fetch person leads from API
  const fetchPersonalLeads = async (page) => {
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLeadsLoading(true);
    try {
      const apiParams = {
        // from_date: "2025-07-25",
        // to_date: "2025-07-25",
        from_date: filters?.lastVisitDate?.start || null,
        to_date: filters?.lastVisitDate?.end || null,
        page: page,
        paginate: true,
        workspace_id:
          JSON.parse(localStorage.getItem("selected_workspace"))
            ?.workspace_id || null,

        //send identity level in api from filters
        ...(filters?.identityLevel?.length > 0 && {
          identity_level: filters.identityLevel.join("|||"),
        }),

        // Add all other filter parameters
        ...(filters?.visitorLocation?.length > 0 && {
          // visitor_location: filters.visitorLocation.join(","),
          // we need to split visitor location into city,state and country and send as separate comma separated values visitor_city, visitor_state, visitor_country
          // location format: "city:CityName-state:StateName-country:CountryName"
          visitor_city: filters.visitorLocation
            .map((loc) => {
              const cityPart = loc
                .split("-")
                .find((part) => part.startsWith("city:"));
              return cityPart ? cityPart.replace("city:", "") : "";
            })
            .filter(Boolean)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(","),
          visitor_state: filters.visitorLocation
            .map((loc) => {
              const statePart = loc
                .split("-")
                .find((part) => part.startsWith("state:"));
              return statePart ? statePart.replace("state:", "") : "";
            })
            .filter(Boolean)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(","),
          visitor_country: filters.visitorLocation
            .map((loc) => {
              const countryPart = loc
                .split("-")
                .find((part) => part.startsWith("country:"));
              return countryPart ? countryPart.replace("country:", "") : "";
            })
            .filter(Boolean)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(","),
        }),
        ...(filters?.designationTitle?.length > 0 && {
          designation_title: filters.designationTitle.join(","),
        }),
        ...(filters?.tags?.length > 0 && { tags: filters.tags.join(",") }),
        ...(filters?.companyHQ?.length > 0 && {
          company_hq: filters.companyHQ.join(","),
        }),
        ...(filters?.industry?.length > 0 && {
          industry: filters.industry.join(","),
        }),
        ...(filters?.sector?.length > 0 && {
          sector: filters.sector.join(","),
        }),

        // Add slider ranges
        ...(filters?.icpScore && {
          icp_score_min: filters.icpScore[0],
          icp_score_max: filters.icpScore[1],
        }),
        ...(filters?.numberOfVisits && {
          number_of_visits_min: filters.numberOfVisits[0],
          number_of_visits_max: filters.numberOfVisits[1],
        }),
        ...(filters?.revenue && {
          revenue_min: filters.revenue[0],
          revenue_max: filters.revenue[1],
        }),
        ...(filters?.employees && {
          employees_min: filters.employees[0],
          employees_max: filters.employees[1],
        }),

        //new or edited filters
        // ...(filters?.visitorLocation?.length > 0 && {
        //   visitorLocation: filters.visitorLocation.join(","),
        // }),
        // ...(filters?.industry?.length > 0 && {
        //   industries: filters.industry.join(","),
        // }),
        // ...(filters?.sector?.length > 0 && {
        //   sectors: filters.sector.join(","),
        // }),

        exactVisitor: filters?.exactVisitor,

        ...(filters?.tech?.length > 0 && {
          tech: filters.tech.join(","),
        }),
        ...(filters?.leadscore && {
          leadscore_min: filters.leadscore[0],
          leadscore_max: filters.leadscore[1],
        }),

        hasPhoneNumber: filters?.hasPhoneNumber,

        ...(filters?.revenue && {
          revenue_min: filters.revenue[0],
          revenue_max: filters.revenue[1],
        }),
      };

      clog("API call params:", apiParams);
      clog("Current filters state:", filters);

      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/visonleads/leads/db`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: apiParams,
        signal: controller.signal,
      });
      if (!response.data.error) {
        // Set leads and pagination info from API
        const {
          data: leads = [],
          totalLeads: total = 0,
          totalPages: pages = 0,
          currentPage: apiPage,
          filterinfo = {},
          is_lead = true,
        } = response.data;

        // Set is_lead flag from API
        setIsLead(is_lead);

        //lead response mapping

        let mappedLeads = leads?.map((lead) => {
          // let exactPerson = lead?.contact ? lead?.contact[0] : {};

          // Object.keys mapping for exactPerson fields if found null or undefined then check same key in lead.contactNew
          // Object.keys(exactPerson || {}).forEach((key) => {
          //   if (!exactPerson[key]) {
          //     exactPerson[key] = lead?.exactPerson
          //       ? lead?.exactPerson[key] || null
          //       : null;
          //     if (
          //       exactPerson["linkedin"] == "false" ||
          //       exactPerson["linkedin"] == false
          //     ) {
          //       exactPerson["linkedin"] =
          //         lead?.exactPerson["linkedinUrl"] || null;
          //     }

          //     if (!exactPerson["domain"]) {
          //       exactPerson["domain"] = lead?.exactPerson?.["domain"] || null;
          //     }
          //   }
          // });

          // Calculate duration from pageVisits
          // const calculatedDuration =
          //   lead?.pageVisits?.reduce(
          //     (sum, visit) => sum + (visit?.totalTime || 0),
          //     0
          //   ) || 0;

          return {
            ...lead,
            // exactPerson: exactPerson,
            lastSession: lead?.summary?.lastSession || {},
            icpScore: parseFloat(lead?.icpScore),
            pisScore: parseFloat(lead?.pisScore),
            summary: {
              ...lead?.summary,
              // duration: calculatedDuration,
              duration: lead?.company?.summary?.duration,
            },
          };
        });

        setAllLeads(mappedLeads);
        // setAllLeads(leads);
        setTotalLeads(total);
        setTotalPagesAPI(pages);
        // Sync current page from API
        if (apiPage && parseInt(apiPage) !== currentPage) {
          setCurrentPage(parseInt(apiPage));
        }
        clog("Filter info from API:", filterinfo);

        // Pass filterinfo to parent component
        if (onFilterInfoChange) {
          onFilterInfoChange(filterinfo);
        }
      } else {
        setIsLead(response?.data?.is_lead || false);
        console.error(response.data.message);
        setsuccessFailureMessage(response?.data?.message);
        setopenFailureModal("dismissible");
      }
    } catch (error) {
      console.error(error);
      if (axios.isCancel(error)) {
        clog("Request canceled:", error.message);
        return;
      }
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("login_token");
        navigate("/");
      }
      setsuccessFailureMessage(
        error?.response?.data?.message || "An error occurred"
      );
      setopenFailureModal("dismissible");
      setAllLeads([]);
      setTotalLeads(0);
      setTotalPagesAPI(0);
      setIsLead(false);
    } finally {
      // Only set to false if this controller is still the current one
      if (abortControllerRef.current === controller) {
        setLeadsLoading(false);
      }
    }
  };

  // export to excel function will call api/visonleads/leads/db api with paginate false and get all leads based on current filters and export to excel using xlsx package
  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const apiParams = {
        from_date: filters?.lastVisitDate?.start || null,
        to_date: filters?.lastVisitDate?.end || null,
        // paginate: false,
        workspace_id:
          JSON.parse(localStorage.getItem("selected_workspace"))
            ?.workspace_id || null,
        // Add all other filter parameters
        ...(filters?.visitorLocation?.length > 0 && {
          // visitor_location: filters.visitorLocation.join(","),
          // we need to split visitor location into city,state and country and send as separate comma separated values visitor_city, visitor_state, visitor_country
          // location format: "city:CityName-state:StateName-country:CountryName"
          visitor_city: filters.visitorLocation
            .map((loc) => {
              const cityPart = loc
                .split("-")
                .find((part) => part.startsWith("city:"));
              return cityPart ? cityPart.replace("city:", "") : "";
            })
            .filter(Boolean)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(","),
          visitor_state: filters.visitorLocation
            .map((loc) => {
              const statePart = loc
                .split("-")
                .find((part) => part.startsWith("state:"));
              return statePart ? statePart.replace("state:", "") : "";
            })
            .filter(Boolean)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(","),
          visitor_country: filters.visitorLocation
            .map((loc) => {
              const countryPart = loc
                .split("-")
                .find((part) => part.startsWith("country:"));
              return countryPart ? countryPart.replace("country:", "") : "";
            })
            .filter(Boolean)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(","),
        }),
        ...(filters?.designationTitle?.length > 0 && {
          designation_title: filters.designationTitle.join(","),
        }),
        ...(filters?.tags?.length > 0 && { tags: filters.tags.join(",") }),
        ...(filters?.companyHQ?.length > 0 && {
          company_hq: filters.companyHQ.join(","),
        }),
        ...(filters?.industry?.length > 0 && {
          industry: filters.industry.join(","),
        }),
        ...(filters?.sector?.length > 0 && {
          sector: filters.sector.join(","),
        }),

        // Add slider ranges
        ...(filters?.icpScore && {
          icp_score_min: filters.icpScore[0],
          icp_score_max: filters.icpScore[1],
        }),
        ...(filters?.numberOfVisits && {
          number_of_visits_min: filters.numberOfVisits[0],
          number_of_visits_max: filters.numberOfVisits[1],
        }),
        ...(filters?.revenue && {
          revenue_min: filters.revenue[0],
          revenue_max: filters.revenue[1],
        }),
        ...(filters?.employees && {
          employees_min: filters.employees[0],
          employees_max: filters.employees[1],
        }),

        //new or edited filters
        // ...(filters?.visitorLocation?.length > 0 && {
        //   visitorLocation: filters.visitorLocation.join(","),
        // }),
        // ...(filters?.industry?.length > 0 && {
        //   industries: filters.industry.join(","),
        // }),
        // ...(filters?.sector?.length > 0 && {
        //   sectors: filters.sector.join(","),
        // }),

        exactVisitor: filters?.exactVisitor,

        ...(filters?.tech?.length > 0 && {
          tech: filters.tech.join(","),
        }),
        ...(filters?.leadscore && {
          leadscore_min: filters.leadscore[0],
          leadscore_max: filters.leadscore[1],
        }),

        hasPhoneNumber: filters?.hasPhoneNumber,

        ...(filters?.revenue && {
          revenue_min: filters.revenue[0],
          revenue_max: filters.revenue[1],
        }),
      };

      clog("API call params:", apiParams);
      clog("Current filters state:", filters);

      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/visonleads/leads/db`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: apiParams,
      });

      if (!response.data.error) {
        // Set leads and pagination info from API
        const { data: leads = [] } = response.data;

        //lead response mapping

        let mappedLeads = leads?.map((lead) => {
          // let exactPerson = lead?.contact ? lead?.contact[0] : {};

          // // Object.keys mapping for exactPerson fields if found null or undefined then check same key in lead.contactNew
          // Object.keys(exactPerson || {}).forEach((key) => {
          //   if (!exactPerson[key]) {
          //     exactPerson[key] = lead?.exactPerson
          //       ? lead?.exactPerson[key] || null
          //       : null;
          //     if (
          //       exactPerson["linkedin"] == "false" ||
          //       exactPerson["linkedin"] == false
          //     ) {
          //       exactPerson["linkedin"] =
          //         lead?.exactPerson["linkedinUrl"] || null;
          //     }

          //     if (!exactPerson["domain"]) {
          //       exactPerson["domain"] = lead?.exactPerson?.["domain"] || null;
          //     }
          //   }
          // });

          // Calculate duration from pageVisits
          // const calculatedDuration =
          //   lead?.pageVisits?.reduce(
          //     (sum, visit) => sum + (visit?.totalTime || 0),
          //     0
          //   ) || 0;

          return {
            ...lead,
            // exactPerson: exactPerson,
            lastSession: lead?.summary?.lastSession || {},
            summary: {
              ...lead?.summary,
              // duration: calculatedDuration,
              duration: lead?.company?.summary?.duration,
            },
          };
        });

        let leadsToExport = mappedLeads.map((lead) => {
          // Helper function to get social URL
          const getSocialUrl = (socialObj, platform) => {
            const url =
              socialObj?.social?.[platform] ||
              socialObj?.social?.[platform + "Url"] ||
              socialObj?.[platform] ||
              socialObj?.[platform + "Url"];
            return url && url !== "false" ? url : "";
          };

          // Base export object with common fields
          const exportData = {
            // Personal Info (only for personal tab)
            "First Name": lead?.exactPerson?.firstName || "",
            "Last Name": lead?.exactPerson?.lastName || "",
            // "Full Name":
            //   (lead?.exactPerson?.firstName || "") +
            //   " " +
            //   (lead?.exactPerson?.lastName || ""),
            "Title/Position": lead?.exactPerson?.position || "",

            // ICP Score (only shown in personal tab)
            ICP: !(lead?.subscription_type == "paid")
              ? ""
              : lead?.icpScore || "",
            // PIS Score (only shown in personal tab)
            PIS: !(lead?.subscription_type == "paid")
              ? ""
              : lead?.pisScore || "",

            // Personal Social Links (only for personal tab)
            "Personal LinkedIn": !(lead?.subscription_type == "paid")
              ? ""
              : getSocialUrl(lead?.exactPerson, "linkedin"),

            Email: !(lead?.subscription_type == "paid")
              ? ""
              : lead?.exactPerson?.businessEmail ||
                lead?.exactPerson?.personalEmail ||
                "",
            Phone: !(lead?.subscription_type == "paid")
              ? ""
              : lead?.exactPerson?.phone
              ? lead?.exactPerson?.phone.includes("+")
                ? lead?.exactPerson?.phone
                : "+" + lead?.exactPerson?.phone
              : "",

            // Company Info (shown in both tabs)
            "Company Name": lead?.company?.name || "",

            //other company info which we show in drawer
            "Company Sector": lead?.company?.sector || "",
            "Company Founded Year": lead?.company?.yearFounded || "",
            "Company Employees": lead?.company?.employeesRange || "",
            "Company Revenue": lead?.company?.revenueRange || "",
            "Company Industry": lead?.company?.industry || "",

            "Company City": lead?.company?.city || "",
            "Company State": lead?.company?.state || "",
            "Company Country": lead?.company?.country || "",

            // Company Social Links (shown in both tabs)
            "Company LinkedIn": getSocialUrl(lead?.company, "linkedin"),
            "Company Twitter": getSocialUrl(lead?.company, "twitter"),
            "Company YouTube": getSocialUrl(lead?.company, "youtube"),
            "Company Crunchbase": getSocialUrl(lead?.company, "crunchbase"),
            "Company Facebook": getSocialUrl(lead?.company, "facebook"),
            "Pages Visited":
              lead?.pageVisits
                ?.map((p) => {
                  if (p?.path?.includes("http")) {
                    return p?.path;
                  } else {
                    return lead?.workspaceName + p?.path;
                  }
                })
                .join(", ") || "",
            "Number of Visits": lead?.summary?.visits || "",

            Duration: lead?.summary?.duration
              ? formatDuration(lead?.summary?.duration)
              : "",
            // "Duration (Seconds)": lead?.summary?.duration
            //   ? Math.floor(lead?.summary?.duration / 1000)
            //   : "",
            // Visit & Engagement Info (shown in both tabs)
            "Last Visit Date": lead?.lastSession?.date
              ? moment(lead.lastSession.date)
                  .utc()
                  .format("MMMM Do YYYY, h:mm:ss a") + " UTC"
              : "",
          };

          return exportData;

          // // Add personal tab specific fields
          // if (activeTab === "personal") {
          //   return {
          //     // Common fields
          //     ...exportData,
          //   };
          // } else {
          //   // Company tab - only common company and engagement fields
          //   return exportData;
          // }
        });
        const worksheet = XLSX.utils.json_to_sheet(leadsToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
        XLSX.writeFile(workbook, "leads.xlsx");
      } else {
        // Handle error case
        console.error("Error exporting leads:", response.data.error);
        setsuccessFailureMessage(response?.data?.message);
        setopenFailureModal("dismissible");
      }
    } catch (error) {
      console.error("Error exporting leads:", error);
      setsuccessFailureMessage("Failed to export leads");
      setopenFailureModal("dismissible");
    } finally {
      setIsExporting(false);
    }
  };

  // Submit lead rating (thumbs up / thumbs down)
  const submitLeadRating = async (leadId, ratting, comments = "") => {
    try {
      setRatingSubmitting(true);
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/lead/ratting`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: { ratting, comments, id: leadId },
      });
      // Show success message from API and refresh leads
      toast('We have successfully captured your rating.', {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
                type: "success",
              });
      setAllLeads(prev => prev.map(lead => lead._id == leadId ? { ...lead, ratting: ratting,comments:comments } : lead));
      // fetchPersonalLeads(currentPage);
    } catch (error) {
      console.error("Error submitting lead rating:", error);
      toast('Something went wrong. Please try again.', {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
                type: "error",
              });
    } finally {
      setRatingSubmitting(false);
      setRatingModalOpen(false);
      setRatingComment("");
      setRatingLeadId(null);
    }
  };

  useEffect(() => {
    fetchPersonalLeads(currentPage);

    // Optional: cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [activeTab, currentPage, filters]);

  // drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);

  // Calculate pagination values
  const totalItems = totalLeads;
  const totalPages = totalPagesAPI;
  const startIndex = (currentPage - 1) * itemsPerPage;
  // end index based on items fetched for current page
  const endIndex = Math.min(startIndex + (allLeads?.length || 0), totalLeads);

  // const shouldHideData = getPlanDetails() == 6;
  // || true;
  // Get current page leads
  // const currentLeads = useMemo(() => {
  //   return allLeads.slice(startIndex, endIndex);
  // }, [allLeads, startIndex, endIndex]);

  // Icon component to use SVG symbols
  const Icon = ({ name, className }) => (
    <svg className={className}>
      <use href={`#icon_${name}`} />
    </svg>
  );

  // No Leads Component
  const NoLeadsComponent = () => {
    clog("NoLeadsComponent rendered");
    clog("Animation data:", noLeadsAnimation);
    clog("is_lead flag:", isLead);

    // Simple logic based on is_lead flag from API:
    // - is_lead === true && empty array: Leads exist in backend but no results match current filters
    // - is_lead === false && empty array: No leads tracked yet by the system
    const showNoResults = isLead && allLeads.length === 0;
    const showNoLeadsTracked = !isLead && allLeads.length === 0;

    return (
      <div className="flex flex-col items-center justify-center  py-1 px-4 h-full">
        {showNoLeadsTracked && noLeadsAnimation ? (
          <Lottie
            animationData={noLeadsAnimation}
            loop={true}
            autoplay={true}
            className="w-full h-full"
            onLoadedData={() => clog("Animation loaded")}
            onError={(error) => console.error("Animation error:", error)}
          />
        ) : showNoLeadsTracked && !noLeadsAnimation ? (
          <div className="text-gray-500 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#5e78f4] to-[#f14ce2] rounded-full flex items-center justify-center">
              <span className="text-white text-2xl">🤖</span>
            </div>
            <p>Animation Loading...</p>
          </div>
        ) : null}

        <div className="text-center max-w-lg">
          {showNoResults ? (
            <>
              <h3 className="text-3xl font-bold text-[#2a2a2a] mb-6 tracking-tight">
                No Results Found
              </h3>
              <p className="text-lg text-[#666666] leading-relaxed font-medium">
                Kwin didn't find any data matching your filters. Try broadening
                your search.
              </p>
            </>
          ) : showNoLeadsTracked ? (
            <>
              <h3 className="text-3xl font-bold text-[#2a2a2a] mb-6 tracking-tight">
                No Leads Tracked Yet
              </h3>
              <p className="text-lg text-[#666666] leading-relaxed font-medium">
                Please check back later in 6 to 12 hours
              </p>
              {/* <div className="mt-8 p-4 bg-gradient-to-r from-[#5e78f4]/10 to-[#f14ce2]/10 rounded-2xl border border-gray-100">
                <p className="text-sm text-[#888888]">
                  Our tracking system is working to identify and collect leads for
                  you
                </p>
              </div> */}
            </>
          ) : null}
        </div>
      </div>
    );
  };

  // User Avatar Component
  const UserAvatar = ({ name, url }) => {
    const [imgError, setImgError] = useState(false);

    // If URL is provided and there's no load error, render the image
    if (url && !imgError) {
      return (
        <img
          src={url}
          alt={name}
          className="w-[110px] h-[110px] rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      );
    }

    // Fallback to initials
    const initials = name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "";

    return (
      <div className="w-[110px] h-[110px] rounded-full bg-gradient-to-br from-[#5e78f4] to-[#f14ce2] flex items-center justify-center text-white text-[32px] font-bold !p-0">
        {initials}
      </div>
    );
  };

  //social icons and link component
  const SocialLink = ({ platform, socialObj, subscription_type }) => {
    const url =
      socialObj?.social?.[platform] ||
      socialObj?.social?.[platform + "Url"] ||
      socialObj?.[platform] ||
      socialObj?.[platform + "Url"];
    if (!url || url == "false") return null;

    const isCompany = socialObj?.name;

    // If should hide data and not company, show tooltip and handle click
    if (subscription_type != "paid" && !isCompany) {
      return (
        <Tooltip
          content="Please upgrade to open social profiles"
          trigger="hover"
          style="light"
          placement="top"
        >
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsUpgradeModalOpen(true);
            }}
            className="flex items-center gap-2.5  cursor-pointer"
          >
            <Icon name={platform} className="w-4 h-4" />
          </div>
        </Tooltip>
      );
    }

    // Normal case - no restrictions
    return (
      <Link
        to={url.startsWith("https://") ? url : `https://${url}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2.5"
      >
        <Icon name={platform} className="w-4 h-4" />
      </Link>
    );
  };

  // Lead Card Component
  const LeadCard = ({ lead, index }) => {
    
    // Check for IntentTopics (try both casings)
    const intentTopics = lead?.IntentTopics || lead?.intentTopics || [];
    const signalTopics = intentTopics.filter(t => t?.topicName);

    return (
    <div className="relative flex items-center gap-5 px-4 py-4 bg-[#FAFBFC] border-b border-[#DCDCDC] flex-wrap">
      {/* Signal Source Badges - shown when lead has IntentTopics with topicName */}
      {signalTopics.length > 0 && (
        <div className="w-full flex flex-wrap items-center gap-2 mb-1 -mt-1">
          <span className="text-[10px] font-bold text-[#6b7280] uppercase tracking-wider">Signal:</span>
          {signalTopics.map((topic, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide shadow-sm"
              style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                color: '#ffffff',
                border: '1px solid rgba(124, 58, 237, 0.3)',
                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.25)',
              }}
            >
              <svg
                className="w-3 h-3 flex-shrink-0"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641l2.5-8.5z"/>
              </svg>
              {topic.topicName}
            </span>
          ))}
        </div>
      )}
      {/* Selection Checkbox */}
      {/* <div className="w-5 h-5 border border-[#2a2a2a] rounded-sm flex items-center justify-center cursor-pointer">
        <Icon name="profile_select" className="w-4 h-4 text-[#2a2a2a]" />
      </div> */}
      <div className="flex flex-col items-center gap-2">
        
      {/* User Avatar */}
      <UserAvatar
        name={
          activeTab === "personal"
            ? [lead?.exactPerson?.firstName, lead?.exactPerson?.lastName]
                .filter(Boolean)
                .join(" ")
            : lead?.company?.name
        }
        url={
          activeTab === "personal"
            ? lead?.exactPerson?.picture
            : lead?.company?.logo
        }
      />
        <p className="text-xs text-[#666666] leading-relaxed font-medium">Rate Data Accuracy</p>

        {/* Thumbs up and down rating */}
        <div className="flex items-center gap-2 mt-1">
          <Tooltip content="Accurate data" trigger="hover" style="light" placement="bottom">
            <button
              onClick={(e) => {
                e.stopPropagation();
                submitLeadRating(lead._id, 1);
              }}
              disabled={ratingSubmitting}
              className={`p-1.5 rounded-full transition-all duration-200 ${
                lead?.ratting == 1
                  ? "bg-green-100 text-green-600"
                  : "hover:bg-gray-100 text-gray-400 hover:text-green-500"
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
            </button>
          </Tooltip>
          <Tooltip content="Inaccurate Data" trigger="hover" style="light" placement="bottom">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setRatingLeadId(lead._id);
                setRatingModalOpen(true);
                setRatingComment(lead?.comments);
              }}
              disabled={ratingSubmitting}
              className={`p-1.5 rounded-full transition-all duration-200 ${
                lead?.ratting == 0
                  ? "bg-red-100 text-red-600"
                  : "hover:bg-gray-100 text-gray-400 hover:text-red-500"
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 15V19a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
                <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
              </svg>
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Lead Information */}
      <div className="flex-1 flex gap-y-3 gap-x-2 flex-wrap  divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
        {activeTab === "personal" ? (
          <>
            {/* Name Section */}
            <div className="flex gap-1">
              <div
                className={
                  lead?.exactPerson?.firstName || lead?.exactPerson?.lastName
                    ? ""
                    : "hidden"
                }
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name="name" className="w-3.5 h-3 text-[#2a2a2a]" />
                  <span className="text-[10px] font-bold text-[#2a2a2a]">
                    Name
                  </span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-[14px] text-[#2a2a2a] font-normal">
                    {[lead?.exactPerson?.firstName, lead?.exactPerson?.lastName]
                      .filter(Boolean)
                      .join(" ") || <>&nbsp;</>}
                  </span>
                </div>
              </div>
              <div className="flex items-center pt-3 gap-2.5">
                <SocialLink
                  subscription_type={lead?.subscription_type}
                  platform="linkedin"
                  socialObj={lead?.exactPerson}
                />

                <SocialLink
                  subscription_type={lead?.subscription_type}
                  platform="twitter"
                  socialObj={lead?.exactPerson}
                />

                <SocialLink
                  subscription_type={lead?.subscription_type}
                  platform="facebook"
                  socialObj={lead?.exactPerson}
                />
              </div>
            </div>

            {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}

            <div className={lead?.icpScore ? "" : "hidden"}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon name="icp_score" className="w-3.5 h-3 text-[#2a2a2a]" />
                <span className="text-[10px] font-bold text-[#2a2a2a]">
                  ICP
                </span>
              </div>
              {lead?.subscription_type != "paid" ? (
                <Tooltip
                  trigger="hover"
                  autoFocus={true}
                  style={"light"}
                  content="Please upgrade to view ICP Score details"
                >
                  <div
                    onClick={() => {
                      setIsUpgradeModalOpen(true);
                    }}
                    className={`flex items-center gap-2.5 blur-sm`}
                  >
                    <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                      <>ICP&nbsp;</>
                    </span>
                    <Icon name="information" className="w-5 h-5 hidden" />
                  </div>
                </Tooltip>
              ) : (
                <div className={`flex items-center gap-2.5 `}>
                  <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] ">
                    {/* {lead?.icpScore || <>&nbsp;</>} */}
                    {lead?.icpScore || <>&nbsp;</>}
                  </span>
                  <Tooltip
                    // className="hidden"
                    trigger="hover"
                    autoFocus={true}
                    style={"light"}
                    className="w-[50%]"
                    // content={
                    //   <div className="bg-[#ffffff]  rounded-md p-2">
                    //     {/* Header */}
                    //     <div className="flex  items-center gap-2.5 mb-[25px]">
                    //       <Icon
                    //         name="matching_factors"
                    //         className="w-[26px] h-5 "
                    //       />
                    //       <span className="font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#000000] leading-none">
                    //         Matching Factors
                    //       </span>
                    //     </div>

                    //     {/* Factors Grid */}
                    //     <div className="flex flex-col gap-7">
                    //       {/* First Row */}
                    //       <div className="flex flex-wrap gap-5">
                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="position"
                    //               className="w-[15px] h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[155px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 High-value position
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 President
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>

                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="company_level"
                    //               className="w-[22px] h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[156px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Small company size
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 31+ employees
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>
                    //       </div>

                    //       {/* Second Row */}
                    //       <div className="flex flex-wrap gap-5">
                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="drawer_industry"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[121px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Industry match
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Oil & Energy
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>

                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="engagement"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[186px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 High engagement
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 8360 seconds duration
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>
                    //       </div>

                    //       {/* Third Row */}
                    //       <div className="flex gap-5">
                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="visits"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[106px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Multiple visits
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 02 Visits
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>

                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] px-[9px] py-[5px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="drawer_revenue"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-3.5 w-[211px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Medium revenue company
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 $1M - $5M
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>
                    //       </div>

                    //       {/* Fourth Row */}
                    //       <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-60 h-20">
                    //         <div className="flex gap-2.5">
                    //           <Icon
                    //             name="geography"
                    //             className="w-3.5 h-5 text-[#2a2a2a]"
                    //           />
                    //           <div className="flex flex-col gap-[9px] w-[143px]">
                    //             <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //               Target geography
                    //             </span>
                    //             <span className="ffont-bold ont-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //               United States
                    //             </span>
                    //           </div>
                    //         </div>
                    //       </div>
                    //     </div>
                    //   </div>
                    // }
                    content={
                      "Ideal Customer Profile (ICP) score is on a scale of 0 to 100. The higher the better. Kwin compares the identified visitor with the ICP approved in the Knowledgebase section."
                    }
                  >
                    <Icon name="information" className="w-5 h-5" />
                  </Tooltip>
                </div>
              )}
            </div>
            {/* pis score */}
            <div className={lead?.pisScore ? "" : "hidden"}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon name="pis_score" className="w-3.5 h-3 text-[#2a2a2a]" />
                <span className="text-[10px] font-bold text-[#2a2a2a]">
                  PIS
                </span>
              </div>
              {lead?.subscription_type != "paid" ? (
                <Tooltip
                  trigger="hover"
                  autoFocus={true}
                  style={"light"}
                  content="Please upgrade to view PIS Score details"
                >
                  <div
                    onClick={() => {
                      setIsUpgradeModalOpen(true);
                    }}
                    className={`flex items-center gap-2.5 blur-sm`}
                  >
                    <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                      <>PIS&nbsp;</>
                    </span>
                    <Icon name="information" className="w-5 h-5 hidden" />
                  </div>
                </Tooltip>
              ) : (
                <div className={`flex items-center gap-2.5 `}>
                  <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00]">
                    {lead?.pisScore || <>&nbsp;</>}
                  </span>
                  <Tooltip
                    // className="hidden"
                    trigger="hover"
                    autoFocus={true}
                    style={"light"}
                    className="w-[50%]"
                    // content={
                    //   <div className="bg-[#ffffff]  rounded-md p-2">
                    //     {/* Header */}
                    //     <div className="flex  items-center gap-2.5 mb-[25px]">
                    //       <Icon
                    //         name="matching_factors"
                    //         className="w-[26px] h-5 "
                    //       />
                    //       <span className="font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#000000] leading-none">
                    //         Matching Factors
                    //       </span>
                    //     </div>

                    //     {/* Factors Grid */}
                    //     <div className="flex flex-col gap-7">
                    //       {/* First Row */}
                    //       <div className="flex flex-wrap gap-5">
                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="position"
                    //               className="w-[15px] h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[155px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 High-value position
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 President
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>

                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="company_level"
                    //               className="w-[22px] h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[156px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Small company size
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 31+ employees
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>
                    //       </div>

                    //       {/* Second Row */}
                    //       <div className="flex flex-wrap gap-5">
                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="drawer_industry"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[121px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Industry match
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Oil & Energy
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>

                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="engagement"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[186px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 High engagement
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 8360 seconds duration
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>
                    //       </div>

                    //       {/* Third Row */}
                    //       <div className="flex gap-5">
                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="visits"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-[9px] w-[106px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Multiple visits
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 02 Visits
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>

                    //         <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] px-[9px] py-[5px] w-[260px] h-20">
                    //           <div className="flex gap-2.5">
                    //             <Icon
                    //               name="drawer_revenue"
                    //               className="w-5 h-5 text-[#2a2a2a]"
                    //             />
                    //             <div className="flex flex-col gap-3.5 w-[211px]">
                    //               <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 Medium revenue company
                    //               </span>
                    //               <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //                 $1M - $5M
                    //               </span>
                    //             </div>
                    //           </div>
                    //         </div>
                    //       </div>

                    //       {/* Fourth Row */}
                    //       <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-60 h-20">
                    //         <div className="flex gap-2.5">
                    //           <Icon
                    //             name="geography"
                    //             className="w-3.5 h-5 text-[#2a2a2a]"
                    //           />
                    //           <div className="flex flex-col gap-[9px] w-[143px]">
                    //             <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //               Target geography
                    //             </span>
                    //             <span className="ffont-bold ont-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                    //               United States
                    //             </span>
                    //           </div>
                    //         </div>
                    //       </div>
                    //     </div>
                    //   </div>
                    // }
                    content={
                      "Purchase Intent Score (PIS) is on a scale of 0 to 100. The higher the better. Kwin scores this based on the identified visitor's browsing behavior on your website."
                    }
                  >
                    <Icon name="information" className="w-5 h-5" />
                  </Tooltip>
                </div>
              )}
            </div>

            {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}
            {/* Title */}
            <div className={lead?.exactPerson?.position ? "" : "hidden"}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon
                  name="designation"
                  className="w-[13px] h-3 text-[#2a2a2a]"
                />
                <span className="text-[10px] font-bold text-[#2a2a2a]">
                  Title
                </span>
              </div>
              <span className="text-[14px] text-[#2a2a2a] font-normal">
                {lead?.exactPerson?.position || ""}
              </span>
            </div>
            {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}
          </>
        ) : (
          <></>
        )}
        {/* Company Name */}
        <div className="flex gap-1  ">
          <div className={lead?.company?.name ? "" : "hidden"}>
            <div className="flex items-center gap-1.5 mb-1">
              <Icon name="company" className="w-3 h-3 text-[#2a2a2a]" />
              <span className="text-[10px] font-bold text-[#2a2a2a]">
                Company Name
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-[14px] text-[#2a2a2a] font-normal">
                {lead?.company?.name || ""}
              </span>
            </div>
          </div>
          <div className="flex items-center pt-3 gap-2.5 ">
            <SocialLink
              subscription_type={lead?.subscription_type}
              platform="linkedin"
              socialObj={lead?.company}
            />

            <SocialLink
              subscription_type={lead?.subscription_type}
              platform="twitter"
              socialObj={lead?.company}
            />

            <SocialLink
              subscription_type={lead?.subscription_type}
              platform="youtube"
              socialObj={lead?.company}
            />

            <SocialLink
              subscription_type={lead?.subscription_type}
              platform="crunchbase"
              socialObj={lead?.company}
            />

            <SocialLink
              subscription_type={lead?.subscription_type}
              platform="facebook"
              socialObj={lead?.company}
            />
          </div>
        </div>
        {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}
        {/* Last Visit Section */}
        <div className={lead.lastSession.date ? "" : "hidden"}>
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="last_visit" className="w-3 h-3 text-[#2a2a2a]" />
            <span className="text-[10px] font-bold text-[#2a2a2a]">
              Last Visit
            </span>
          </div>
          <Tooltip
            trigger="hover"
            autoFocus={true}
            style={"light"}
            placement="right"
            // content = "time convertion" title and below show time in UTC and time in local (users timezone)
            content={
              <div className="text-left">
                {/* title -  Time Conversion */}
                <h4 className="font-bold mb-2">Time Conversion</h4>
                <hr />

                <div>
                  <span className="font-bold">UTC:</span>{" "}
                  {lead?.lastSession?.date
                    ? moment(lead.lastSession.date)
                        .utc()
                        .format("MMMM Do YYYY, h:mm:ss a")
                    : ""}
                </div>
                <hr />
                <div>
                  <span className="font-bold">
                    {
                      // computers gmt offset ex. GMT+5:30
                      moment.tz.zone(moment.tz.guess()).abbr(new Date())
                    }{" "}
                    ● Your Computer:
                  </span>{" "}
                  {lead?.lastSession?.date
                    ? moment(lead.lastSession.date)
                        .local()
                        .format("MMMM Do YYYY, h:mm:ss a z")
                    : ""}
                </div>
              </div>
            }
          >
            <span className="text-[14px] text-[#2a2a2a] font-normal">
              {lead?.lastSession?.date
                ? moment(lead.lastSession.date)
                    .utc()
                    .format("MMMM Do YYYY, h:mm:ss a") + " UTC"
                : ""}
              {/* icon_eye  */}
              <svg className="w-4 h-4 inline-block ml-1">
                <use href={`#icon_eye`} />
              </svg>
            </span>
          </Tooltip>
        </div>

        {/* Visitor Browsing Location */}
        <div
          className={
            [
              lead?.lastSession?.city,
              lead?.lastSession?.state,
              lead?.lastSession?.country,
            ].filter(Boolean).length
              ? ""
              : "hidden"
          }
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="drawer_location" className="w-3 h-3 text-[#2a2a2a]" />
            <span className="text-[10px] font-bold text-[#2a2a2a]">
              Visitor Browsing Location
            </span>
          </div>
          <span className="text-[14px] text-[#2a2a2a] font-normal">
            {[
              lead?.lastSession?.city,
              lead?.lastSession?.state,
              lead?.lastSession?.country,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
        </div>
        {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}
        {/* duration */}
        <div className={lead?.summary?.duration ? "" : "hidden"}>
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="duration" className="w-[11px] h-3 text-[#2a2a2a]" />
            <span className="text-[10px] font-bold text-[#2a2a2a]">
              Duration
            </span>
          </div>
          <span className="text-[14px] text-[#2a2a2a] font-normal">
            {formatDuration(lead?.summary?.duration || 0)}
            {/* {formatDuration(lead?.pageVisits || [])} */}
          </span>
        </div>
        {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}
        {/* number of visits */}
        <div className={lead?.summary?.visits ? "" : "hidden"}>
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="visits" className="w-[18px] h-3 text-[#2a2a2a]" />
            <span className="text-[10px] font-bold text-[#2a2a2a]">
              Number of Visit
            </span>
          </div>
          <span className="text-[14px] text-[#2a2a2a] font-normal">
            {lead?.summary?.visits || <>&nbsp;</>}
          </span>
        </div>
        {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}
        {/* location */}
        <div
          className={
            lead?.company?.city ||
            lead?.company?.state ||
            lead?.company?.country
              ? ""
              : "hidden"
          }
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="location" className="w-[11px] h-3 text-[#2a2a2a]" />
            <span className="text-[10px] font-bold text-[#2a2a2a]">
              Company HQ Location
            </span>
          </div>
          <span className="text-[14px] text-[#2a2a2a] font-normal">
            {[lead?.company?.city, lead?.company?.state, lead?.company?.country]
              .filter(Boolean)
              .join(", ")}
          </span>
        </div>
        {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}
        {activeTab === "personal" ? (
          <>
            {/* Email */}
            <div
              className={
                lead?.exactPerson?.businessEmail ||
                lead?.exactPerson?.personalEmail
                  ? ""
                  : "hidden"
              }
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon name="email" className="w-3 h-3 text-[#2a2a2a]" />
                <span className="text-[10px] font-bold text-[#2a2a2a]">
                  Email
                </span>
              </div>
              {[
                lead?.exactPerson?.businessEmail,
                lead?.exactPerson?.personalEmail,
              ]
                .filter(Boolean)
                .map((email, idx) =>
                  lead?.subscription_type != "paid" ? (
                    <>
                      <Tooltip
                        content="Please upgrade to view email"
                        trigger="hover"
                        style="light"
                        placement="top"
                      >
                        <div
                          onClick={() => {
                            setIsUpgradeModalOpen(true);
                          }}
                          className="blur-sm cursor-pointer"
                        >
                          <p
                            key={idx}
                            className="text-[14px] text-[#2a2a2a] font-normal underline"
                          >
                            placeholder@email.test
                          </p>
                        </div>
                        {idx < 1 && <br />}
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <a
                        key={idx}
                        href={`mailto:${email}`}
                        className="text-[14px] text-[#2a2a2a] font-normal underline"
                      >
                        {email}
                      </a>
                      {idx < 1 && <br />}
                    </>
                  )
                )}
            </div>

            {/* <div className="w-[1px] h-16 bg-[#e5e5e5] mx-4"></div> */}

            {/* phone */}
            <div className={lead?.exactPerson?.phone ? "" : "hidden"}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon name="phone" className="w-3 h-3 text-[#2a2a2a]" />
                <span className="text-[10px] font-bold text-[#2a2a2a]">
                  Phone
                </span>
              </div>
              {lead?.subscription_type != "paid" ? (
                <>
                  <Tooltip
                    content="Please upgrade to view phone number"
                    trigger="hover"
                    style="light"
                    placement="top"
                  >
                    <div
                      onClick={() => {
                        setIsUpgradeModalOpen(true);
                      }}
                      className="blur-sm cursor-pointer"
                    >
                      <p className="text-[14px] text-[#2a2a2a] font-normal underline">
                        +1 (555) 555-5555
                      </p>
                    </div>
                  </Tooltip>
                </>
              ) : (
                <>
                  <a
                    href={`tel:${
                      lead?.exactPerson?.phone
                        ? lead?.exactPerson?.phone.includes("+")
                          ? lead?.exactPerson?.phone
                          : "+" + lead?.exactPerson?.phone
                        : ""
                    }`}
                    className="text-[14px] text-[#2a2a2a] font-normal underline"
                  >
                    {lead?.exactPerson?.phone
                      ? lead?.exactPerson?.phone.includes("+")
                        ? lead?.exactPerson?.phone
                        : "+" + lead?.exactPerson?.phone
                      : ""}
                  </a>
                </>
              )}
            </div>
          </>
        ) : (
          <></>
        )}
      </div>

      {/* Action Buttons */}
      <div className="w-[200px] flex flex-col gap-5">
        {/* View Details Button - Figma Design with Animations */}
        <button
          onClick={() => {
            setIsDrawerOpen(true);
            setDrawerData(lead);
            clog(lead);
          }}
          className="relative w-full h-11 rounded-full overflow-hidden group transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
        >
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#5e78f4] to-[#f14ce2] transition-all duration-300 group-hover:from-[#6b85f5] group-hover:to-[#f45be9]"></div>

          {/* Button Content Container - Exact Figma Layout */}
          <div
            onClick={() => {
              setIsDrawerOpen(true);
              setDrawerData(lead);
              // clog(lead);
            }}
            className="relative box-border content-stretch flex flex-row gap-[23px] items-center justify-start pl-[53px] pr-2.5 py-2.5 w-full h-full"
          >
            {/* Text */}
            <span className="font-bold leading-[1.5] text-[#ffffff] text-[16px] text-left tracking-[-0.176px] transition-all duration-200 group-hover:translate-x-0.5">
              View Details
            </span>

            {/* Arrow Icon Container */}
            <div className="h-0 relative shrink-0 w-5 transition-all duration-300 group-hover:translate-x-1">
              <div className="absolute bottom-[-7.364px] left-0 right-[-5%] top-[-7.364px]">
                <Icon
                  name="arrow_right"
                  className="w-full h-full text-white transition-all duration-300 group-hover:text-white/90"
                />
              </div>
            </div>
          </div>

          {/* Subtle shine effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out"></div>
        </button>

        {/* Enrich With Eva Button */}
        {/* <button className="relative w-full h-11 bg-[#e9e9e9] rounded-full overflow-hidden">
          <div className="flex items-center justify-center gap-3 px-[41px] py-2.5">
            <span className="text-[16px] font-bold text-[#2a2a2a] tracking-[-0.176px]">
              Enrich With Eva
            </span>
            <Icon name="arrow_right" className="w-5 h-0 text-[#2a2a2a]" />
          </div>
        </button> */}
      </div>
    </div>
   );
  };

  // Pagination Component - Pixel Perfect Figma Design with Full Functionality
  const Pagination = () => {
    // Calculate visible page numbers
    const getVisiblePages = () => {
      const delta = 2; // Number of pages to show on each side of current page
      const range = [];

      // Always show first page
      range.push(1);

      // Calculate start and end for middle range
      const start = Math.max(2, currentPage - delta);
      const end = Math.min(totalPages - 1, currentPage + delta);

      // Add ellipsis after 1 if needed
      if (start > 2) {
        range.push("...");
      }

      // Add middle range
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          range.push(i);
        }
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        range.push("...");
      }

      // Always show last page if more than 1 page
      if (totalPages > 1) {
        range.push(totalPages);
      }

      return range;
    };

    const visiblePages = getVisiblePages();

    const handlePageChange = (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        setCurrentPage(page);
      }
    };

    const handlePrevious = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };

    const handleNext = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };

    return (
      <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[60px] h-[60px] flex items-center justify-center gap-[32px] px-[30px] py-3 min-w-fit shadow-xl">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={`flex items-center gap-[21px] transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
            currentPage === 1 ? "opacity-50" : "hover:opacity-80"
          }`}
        >
          {/* Previous Arrow SVG */}
          <svg
            width="11"
            height="20"
            viewBox="0 0 11 20"
            fill="none"
            className="text-[#2a2a2a]"
          >
            <path
              d="M9.5 18L1.5 10L9.5 2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[14px] text-[#2a2a2a] font-normal">
            Previous
          </span>
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-8">
          {visiblePages.map((page, index) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="text-[18px] font-semibold text-[#2a2a2a] px-1"
                >
                  .....
                </span>
              );
            }

            const isActive = page === currentPage;

            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-[30px] h-[30px] rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                  isActive
                    ? "bg-[#5e78f4] text-white shadow-lg"
                    : "text-[#2a2a2a] hover:bg-[#f0f0f0] hover:text-[#5e78f4]"
                }`}
              >
                <span className="text-[18px] font-semibold">{page}</span>
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={`flex items-center gap-[21px] transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
            currentPage === totalPages ? "opacity-50" : "hover:opacity-80"
          }`}
        >
          <span className="text-[14px] text-[#2a2a2a] font-normal">Next</span>
          {/* Next Arrow SVG */}
          <svg
            width="11"
            height="20"
            viewBox="0 0 11 20"
            fill="none"
            className="text-[#2a2a2a]"
          >
            <path
              d="M1.5 18L9.5 10L1.5 2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    );
  };

  // Commented out - now using common DetailsDrawer component
  // Wrapped in if(false) to keep code but prevent execution
  if (false) {
    const DetailsDrawer_OLD = () => {
      const [activeDrawerTab, setActiveDrawerTab] = useState(
        activeTab === "personal" ? "visitor" : "company"
      );

      // Helper function to scroll to section
      const scrollToSection = (sectionId) => {
        setActiveDrawerTab(sectionId);
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      };

      // Sample data - in real app this would come from drawerData prop
      const visitorData =
        drawerData?.exactPerson ||
        {
          // name: "Vineet Joshi",
          // title: "Abc xyz",
          // email: "Vineet@joshi.com",
          // lastSeen: "1 Days Ago",
          // contactStage: "Abc xyz",
          // icpScore: "06",
          // recommendation: "High-quality lead - prioritize for immediate follow-up",
        };

      const companyData =
        drawerData?.company ||
        {
          // name: "Company Pvt Ltd",
          // yearFounded: "Since 1990",
          // totalEmployees: "700",
          // sector: "700",
          // revenue: "5Cr",
          // location: "Abc Location",
          // industry: "Abc Industry",
        };

      return (
        <Drawer
          open={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setDrawerData(null);
          }}
          position="right"
          className="p-0 w-4/5 notification-canvas"
        >
          <div className="h-full flex flex-col bg-white">
            {/* Header with Tabs */}
            <div className="sticky flex top-0 z-10 bg-white border-b border-[#dcdcdc] h-[90px] items-center">
              {/* Tab Navigation */}
              <div className="flex flex-1 items-center h-full">
                {activeTab === "personal" ? (
                  <>
                    {/* Visitor Tab */}
                    <button
                      onClick={() => scrollToSection("visitor")}
                      className={`flex items-center gap-2.5 h-full px-10 ${
                        activeDrawerTab === "visitor"
                          ? "text-[#097ba9] bg-[#E9F2FF]"
                          : "text-[#2a2a2a]"
                      } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
                    >
                      <Icon name="drawer_visitor" className="w-4 h-4" />
                      <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                        Visitor
                      </span>
                    </button>
                  </>
                ) : (
                  <></>
                )}

                {/* Company Tab */}
                <button
                  onClick={() => scrollToSection("company")}
                  className={`flex items-center gap-2.5 h-full px-10 ${
                    activeDrawerTab === "company"
                      ? "text-[#097ba9] bg-[#E9F2FF]"
                      : "text-[#2a2a2a]"
                  } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
                >
                  <Icon name="company" className="w-4 h-4" />
                  <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                    Company
                  </span>
                </button>

                {/* Page Visits Tab */}
                <button
                  onClick={() => scrollToSection("pageVisits")}
                  className={`flex items-center gap-2.5 h-full px-10 ${
                    activeDrawerTab === "pageVisits"
                      ? "text-[#097ba9] bg-[#E9F2FF]"
                      : "text-[#2a2a2a]"
                  } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
                >
                  <Icon name="visits" className="w-4 h-4" />
                  <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                    Page Visits
                  </span>
                </button>

                {/* Activity Tab */}
                {/* <button
                onClick={() => scrollToSection("activity")}
                className={`flex items-center gap-2.5 h-full px-10 ${
                  activeDrawerTab === "activity"
                    ? "text-[#097ba9] bg-[#E9F2FF]"
                    : "text-[#2a2a2a]"
                } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
              >
                <Icon name="drawer_activity" className="w-4 h-4" />
                <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                  Activity
                </span>
              </button> */}
              </div>
              {/* Close Button */}
              <button
                onClick={() => {
                  setIsDrawerOpen(false);
                  setDrawerData(null);
                }}
                className=" w-10 h-10 flex items-center justify-center text-[#2a2a2a] hover:bg-[#f0f0f0] rounded-full transition-colors duration-200 mr-2"
              >
                <Icon name="close" className="w-10 h-10" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-auto px-[60px] py-[40px]">
              {activeTab === "personal" ? (
                <>
                  {/* Visitor Section */}
                  <section id="visitor" className="mb-[50px]">
                    <div className="flex items-center gap-2.5 mb-[30px]">
                      <Icon
                        name="drawer_visitor"
                        className="w-[15px] h-5 text-[#000000]"
                      />
                      <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                        Visitors
                      </h2>
                    </div>

                    <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                      <div className="flex  items-center mb-[30px]  gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
                        {/* User Avatar */}
                        <UserAvatar
                          name={
                            [visitorData?.firstName, visitorData?.lastName]
                              .filter(Boolean)
                              .join(" ") || ""
                          }
                          url={visitorData?.picture}
                        />

                        {/* Basic Info */}

                        <div className="flex flex-col gap-2.5 ">
                          <h3
                            style={{
                              display:
                                visitorData?.firstName || visitorData?.lastName
                                  ? "block"
                                  : "none",
                            }}
                            className="font-bold font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#2a2a2a]"
                          >
                            {[visitorData?.firstName, visitorData?.lastName]
                              .filter(Boolean)
                              .join(" ") || <>&nbsp;</>}
                          </h3>
                          <div className="flex items-center gap-[8px]">
                            <SocialLink
                              subscription_type={drawerData?.subscription_type}
                              platform="linkedin"
                              socialObj={visitorData}
                            />

                            <SocialLink
                              subscription_type={drawerData?.subscription_type}
                              platform="twitter"
                              socialObj={visitorData}
                            />

                            <SocialLink
                              subscription_type={drawerData?.subscription_type}
                              platform="facebook"
                              socialObj={visitorData}
                            />
                          </div>
                        </div>

                        {/* Vertical Divider */}
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* ICP Score */}
                        <div
                          style={{
                            display: drawerData?.icpScore ? "block" : "none",
                          }}
                          className="flex flex-col gap-[5px] "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="icp_score"
                              className="w-6 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              ICP Score
                            </span>
                          </div>
                          {drawerData?.subscription_type != "paid" ? (
                            <Tooltip
                              trigger="hover"
                              autoFocus={true}
                              style={"light"}
                              content="Please upgrade to view ICP Score details"
                            >
                              <div
                                className={`flex items-center gap-2.5 blur-sm cursor-pointer`}
                                onClick={() => {
                                  setIsUpgradeModalOpen(true);
                                }}
                              >
                                <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                                  ICP&nbsp;
                                </span>

                                <Icon
                                  name="information"
                                  className="w-5 h-5 hidden"
                                />
                              </div>
                            </Tooltip>
                          ) : (
                            <div className={`flex items-center gap-2.5`}>
                              <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                                {drawerData?.icpScore || <>&nbsp;</>}
                                {/* {drawerData?.icpScore || <>&nbsp;</>} */}
                              </span>
                              <Tooltip
                                className="hidden"
                                trigger="click"
                                autoFocus={true}
                                style={"light"}
                                content={
                                  <div className="bg-[#ffffff]  rounded-md p-2">
                                    {/* Header */}
                                    <div className="flex  items-center gap-2.5 mb-[25px]">
                                      <Icon
                                        name="matching_factors"
                                        className="w-[26px] h-5 "
                                      />
                                      <span className="font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#000000] leading-none">
                                        Matching Factors
                                      </span>
                                    </div>

                                    {/* Factors Grid */}
                                    <div className="flex flex-col gap-7">
                                      {/* First Row */}
                                      <div className="flex flex-wrap gap-5">
                                        <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                          <div className="flex gap-2.5">
                                            <Icon
                                              name="position"
                                              className="w-[15px] h-5 text-[#2a2a2a]"
                                            />
                                            <div className="flex flex-col gap-[9px] w-[155px]">
                                              <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                High-value position
                                              </span>
                                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                President
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                          <div className="flex gap-2.5">
                                            <Icon
                                              name="company_level"
                                              className="w-[22px] h-5 text-[#2a2a2a]"
                                            />
                                            <div className="flex flex-col gap-[9px] w-[156px]">
                                              <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                Small company size
                                              </span>
                                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                31+ employees
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Second Row */}
                                      <div className="flex flex-wrap gap-5">
                                        <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                          <div className="flex gap-2.5">
                                            <Icon
                                              name="drawer_industry"
                                              className="w-5 h-5 text-[#2a2a2a]"
                                            />
                                            <div className="flex flex-col gap-[9px] w-[121px]">
                                              <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                Industry match
                                              </span>
                                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                Oil & Energy
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                          <div className="flex gap-2.5">
                                            <Icon
                                              name="engagement"
                                              className="w-5 h-5 text-[#2a2a2a]"
                                            />
                                            <div className="flex flex-col gap-[9px] w-[186px]">
                                              <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                High engagement
                                              </span>
                                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                8360 seconds duration
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Third Row */}
                                      <div className="flex gap-5">
                                        <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-[260px] h-20">
                                          <div className="flex gap-2.5">
                                            <Icon
                                              name="visits"
                                              className="w-5 h-5 text-[#2a2a2a]"
                                            />
                                            <div className="flex flex-col gap-[9px] w-[106px]">
                                              <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                Multiple visits
                                              </span>
                                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                02 Visits
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] px-[9px] py-[5px] w-[260px] h-20">
                                          <div className="flex gap-2.5">
                                            <Icon
                                              name="drawer_revenue"
                                              className="w-5 h-5 text-[#2a2a2a]"
                                            />
                                            <div className="flex flex-col gap-3.5 w-[211px]">
                                              <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                Medium revenue company
                                              </span>
                                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                                $1M - $5M
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Fourth Row */}
                                      <div className="bg-[#fafbfc] border border-[#e9ebf0] rounded-[10px] p-[10px] w-60 h-20">
                                        <div className="flex gap-2.5">
                                          <Icon
                                            name="geography"
                                            className="w-3.5 h-5 text-[#2a2a2a]"
                                          />
                                          <div className="flex flex-col gap-[9px] w-[143px]">
                                            <span className="font-['Axiforma:Regular',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                              Target geography
                                            </span>
                                            <span className="ffont-bold ont-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a] leading-none">
                                              United States
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                }
                              >
                                <Icon
                                  name="information"
                                  className="w-5 h-5 hidden"
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>

                        {/* Vertical Divider */}
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* Recommendation */}
                        <div
                          style={{
                            display: visitorData.recommendation
                              ? "block"
                              : "none",
                          }}
                          className=" flex-col gap-2.5  hidden"
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_recommendation"
                              className="w-5 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Recommendation
                            </span>
                          </div>
                          <p className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a] leading-normal">
                            {visitorData.recommendation || <>&nbsp;</>}
                          </p>
                        </div>
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] hidden"></div> */}

                        {/* Title */}
                        <div
                          style={{
                            display: visitorData?.position ? "block" : "none",
                          }}
                          className="flex items-center "
                        >
                          <div className="flex flex-col gap-2.5 flex-1">
                            <div className="flex items-center gap-2.5">
                              <Icon
                                name="drawer_title"
                                className="w-[23px] h-5 text-[#2a2a2a]"
                              />
                              <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                                Title
                              </span>
                            </div>
                            <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                              {visitorData?.position || <>&nbsp;</>}
                            </span>
                          </div>
                        </div>
                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}

                        {/* Email */}
                        <div
                          style={{
                            display:
                              visitorData?.businessEmail ||
                              visitorData?.personalEmail
                                ? "block"
                                : "none",
                          }}
                          className="flex flex-col gap-2.5 "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="email"
                              className="w-5 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Email Address
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {/* <a
                            href={`mailto:${visitorData?.businessEmail}`}
                            className="text-[14px] text-[#2a2a2a] font-normal underline"
                          >
                            {visitorData?.businessEmail}
                          </a>
                          <br />
                          <a
                            href={`mailto:${visitorData?.personalEmail}`}
                            className="text-[14px] text-[#2a2a2a] font-normal underline"
                          >
                            {visitorData?.personalEmail}
                          </a> */}
                            {[
                              visitorData?.businessEmail ||
                                visitorData?.personalEmail,
                            ]
                              .filter(Boolean)
                              .map((email, idx) =>
                                drawerData?.subscription_type != "paid" ? (
                                  <>
                                    <Tooltip
                                      content="Please upgrade to view email"
                                      trigger="hover"
                                      style="light"
                                      placement="top"
                                    >
                                      <div
                                        onClick={() => {
                                          setIsUpgradeModalOpen(true);
                                        }}
                                        className="blur-sm cursor-pointer"
                                      >
                                        <p
                                          key={idx}
                                          className=" text-[#2a2a2a] font-normal underline"
                                        >
                                          placeholder@email.test
                                        </p>
                                      </div>
                                      {idx < 1 && <br />}
                                    </Tooltip>
                                  </>
                                ) : (
                                  <>
                                    <a
                                      key={idx}
                                      href={`mailto:${email}`}
                                      className=" text-[#2a2a2a] font-normal underline"
                                    >
                                      {email}
                                    </a>
                                    {idx < 1 && <br />}
                                  </>
                                )
                              )}
                          </span>
                        </div>

                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}
                        {/* Phone */}
                        <div
                          style={{
                            display: visitorData?.phone ? "block" : "none",
                          }}
                          className="flex flex-col gap-2.5 "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="phone"
                              className="w-5 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Phone
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {drawerData?.subscription_type != "paid" ? (
                              <>
                                <Tooltip
                                  content="Please upgrade to view phone number"
                                  trigger="hover"
                                  style="light"
                                  placement="top"
                                >
                                  <div
                                    onClick={() => {
                                      setIsUpgradeModalOpen(true);
                                    }}
                                    className="blur-sm cursor-pointer"
                                  >
                                    <p className=" text-[#2a2a2a] font-normal underline">
                                      +1 (555) 555-5555
                                    </p>
                                  </div>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <a
                                  href={`tel:${
                                    visitorData?.phone
                                      ? "+" + visitorData?.phone
                                      : ""
                                  }`}
                                  className=" text-[#2a2a2a] font-normal underline"
                                >
                                  {visitorData?.phone
                                    ? "+" + visitorData?.phone
                                    : ""}
                                </a>
                              </>
                            )}
                          </span>
                        </div>

                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* Last Seen */}
                        <div
                          style={{
                            display: drawerData?.lastSession?.date
                              ? "block"
                              : "none",
                          }}
                          className="flex flex-col gap-2.5 "
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_clock"
                              className="w-[33px] h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Last Seen
                            </span>
                          </div>
                          <Tooltip
                            trigger="hover"
                            autoFocus={true}
                            style={"light"}
                            placement="right"
                            content={
                              <div className="text-left">
                                {/* title -  Time Conversion */}
                                <h4 className="font-bold mb-2">
                                  Time Conversion
                                </h4>
                                <hr />

                                <div>
                                  <span className="font-bold">UTC:</span>{" "}
                                  {drawerData?.lastSession?.date
                                    ? moment(drawerData.lastSession.date)
                                        .utc()
                                        .format("MMMM Do YYYY, h:mm:ss a")
                                    : ""}
                                </div>
                                <hr />
                                <div>
                                  <span className="font-bold">
                                    {
                                      // computers timezone name ex. IST, PST
                                      moment.tz
                                        .zone(moment.tz.guess())
                                        .abbr(new Date())
                                    }{" "}
                                    ● Your Computer:
                                  </span>{" "}
                                  {drawerData?.lastSession?.date
                                    ? moment(drawerData.lastSession.date)
                                        .local()
                                        .format("MMMM Do YYYY, h:mm:ss a z")
                                    : ""}
                                </div>
                              </div>
                            }
                          >
                            <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                              {drawerData?.lastSession?.date ? (
                                moment(drawerData?.lastSession?.date)
                                  .utc()
                                  .format("MMMM Do YYYY, h:mm:ss a") + " UTC"
                              ) : (
                                <>&nbsp;</>
                              )}
                              {/* icon_eye  */}
                              <svg className="w-6 h-6 inline-block ml-1">
                                <use href={`#icon_eye`} />
                              </svg>
                            </span>
                          </Tooltip>
                        </div>

                        {/* Visitor Browsing Location */}
                        <div
                          style={{
                            display:
                              [
                                drawerData?.lastSession?.city,
                                drawerData?.lastSession?.state,
                                drawerData?.lastSession?.country,
                              ].filter(Boolean).length > 0
                                ? "block"
                                : "none",
                          }}
                          className="flex flex-col gap-2.5"
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_location"
                              className="w-4 h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Visitor Browsing Location
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {[
                              drawerData?.lastSession?.city,
                              drawerData?.lastSession?.state,
                              drawerData?.lastSession?.country,
                            ]
                              .filter(Boolean)
                              .join(", ") || <>&nbsp;</>}
                          </span>
                        </div>

                        {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}

                        {/* {/* Contact Stage */}
                        {/* <div className="flex flex-col gap-2.5 ">
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_stage"
                            className="w-[18px] h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Contact Stage
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {visitorData.contactStage || <>&nbsp;</>}
                        </span>
                      </div>  */}
                      </div>
                    </div>
                  </section>
                  {/* Divider Line */}
                  <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>
                </>
              ) : (
                <></>
              )}

              {/* Company Section */}

              <section id="company" className="mb-[50px]">
                <div className="flex items-center gap-2.5 mb-[30px]">
                  <Icon name="company" className="w-5 h-5 text-[#000000]" />
                  <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                    Company
                  </h2>
                </div>

                <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                  <div className="flex  items-center mb-[30px]  gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
                    {/* <div className="w-[120px] h-[120px] rounded-full overflow-hidden">
                    <img
                      src={companyData?.logo || "#"}
                      alt="Company Logo"
                      className="w-full h-full object-cover"
                    />
                  </div> */}
                    <UserAvatar
                      name={companyData?.name || ""}
                      url={companyData?.logo || ""}
                    />
                    <div className="flex flex-col gap-2.5 flex-1">
                      <h3
                        style={{
                          display: companyData?.name ? "block" : "none",
                        }}
                        className="font-bold font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#2a2a2a]"
                      >
                        {companyData?.name || <>&nbsp;</>}
                      </h3>
                      <div className="flex items-center gap-[30px]">
                        <SocialLink
                          subscription_type={drawerData?.subscription_type}
                          platform="linkedin"
                          socialObj={companyData}
                        />

                        <SocialLink
                          subscription_type={drawerData?.subscription_type}
                          platform="twitter"
                          socialObj={companyData}
                        />

                        <SocialLink
                          subscription_type={drawerData?.subscription_type}
                          platform="youtube"
                          socialObj={companyData}
                        />

                        <SocialLink
                          subscription_type={drawerData?.subscription_type}
                          platform="crunchbase"
                          socialObj={companyData}
                        />

                        <SocialLink
                          subscription_type={drawerData?.subscription_type}
                          platform="facebook"
                          socialObj={companyData}
                        />
                      </div>
                    </div>
                    {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}
                    <div
                      style={{
                        display: companyData?.sector ? "block" : "none",
                      }}
                      className="flex flex-col gap-2.5 px-2.5"
                    >
                      <div className="flex items-center gap-[10px]">
                        <Icon
                          name="drawer_sector"
                          className="w-5 h-5 text-[#2a2a2a]"
                        />
                        <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                          Sector
                        </span>
                      </div>
                      <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                        {companyData?.sector || <>&nbsp;</>}
                      </span>
                    </div>
                    {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc]"></div> */}
                    <div
                      style={{
                        display:
                          companyData?.city ||
                          companyData?.state ||
                          companyData?.country
                            ? "block"
                            : "none",
                      }}
                      className="flex flex-col gap-2.5"
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon
                          name="drawer_location"
                          className="w-4 h-5 text-[#2a2a2a]"
                        />
                        <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                          Company HQ Location
                        </span>
                      </div>
                      <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                        {[
                          companyData?.city,
                          companyData?.state,
                          companyData?.country,
                        ]
                          ?.filter(Boolean)
                          .join(", ") || <>&nbsp;</>}
                      </span>
                    </div>
                    {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                    <div
                      style={{
                        display: companyData?.yearFounded ? "block" : "none",
                      }}
                      className="flex flex-col gap-2.5 "
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon
                          name="drawer_year"
                          className="w-[19px] h-5 text-[#2a2a2a]"
                        />
                        <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                          Year Founded
                        </span>
                      </div>
                      <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                        {companyData?.yearFounded || <>&nbsp;</>}
                      </span>
                    </div>
                    {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                    <div
                      style={{
                        display: companyData?.employeesRange ? "block" : "none",
                      }}
                      className="flex flex-col gap-2.5 "
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon
                          name="drawer_employees"
                          className="w-5 h-5 text-[#2a2a2a]"
                        />
                        <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                          Total Employee
                        </span>
                      </div>
                      <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                        {companyData?.employeesRange || <>&nbsp;</>}
                      </span>
                    </div>
                    {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                    <div
                      style={{
                        display: companyData?.revenueRange ? "block" : "none",
                      }}
                      className="flex flex-col gap-2.5"
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon
                          name="drawer_revenue"
                          className="w-5 h-5 text-[#2a2a2a]"
                        />
                        <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                          Revenue
                        </span>
                      </div>
                      <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                        {companyData?.revenueRange || <>&nbsp;</>}
                        {/* {companyData?.estimatedAnnualRevenue || <>&nbsp;</>} */}
                      </span>
                    </div>
                    {/* <div className="w-[1px] h-[61px] bg-[#dcdcdc] ml-4"></div> */}
                    <div
                      style={{
                        display: companyData?.industry ? "block" : "none",
                      }}
                      className="flex flex-col gap-2.5 w-[124px]"
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon
                          name="drawer_industry"
                          className="w-5 h-5 text-[#2a2a2a]"
                        />
                        <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                          Industry
                        </span>
                      </div>
                      <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                        {companyData?.industry || <>&nbsp;</>}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Divider Line */}
              <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>

              {/* Page Visits Section */}
              <section id="pageVisits" className="mb-[50px]">
                <div className="flex items-center gap-2.5 mb-[30px]">
                  <Icon name="visits" className="w-5 h-5 text-[#000000]" />
                  <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                    Page Visits
                  </h2>
                </div>

                <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                  {drawerData?.pageVisits &&
                  drawerData.pageVisits.length > 0 ? (
                    <div className="space-y-4">
                      {drawerData?.pageVisits.map((visit, index) => (
                        <div
                          key={index}
                          className="bg-white border border-[#e9ebf0] rounded-[20px] p-5 hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="flex items-center justify-between gap-4 flex-wrap">
                            {/* Path */}
                            <div className="flex flex-col gap-2 flex-1 min-w-[200px]">
                              <div className="flex items-center gap-2">
                                <Icon
                                  name="globe"
                                  className="w-4 h-4 text-[#2a2a2a]"
                                />
                                <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                  Page Path
                                </span>
                              </div>
                              <span className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#097ba9] break-all">
                                {visit?.path?.includes("http")
                                  ? visit?.path
                                  : drawerData?.workspaceName + visit?.path}
                              </span>
                            </div>

                            {/* Visit Count */}
                            <div className="flex flex-col gap-2 min-w-[120px]">
                              <div className="flex items-center gap-2">
                                <Icon
                                  name="visits"
                                  className="w-4 h-4 text-[#2a2a2a]"
                                />
                                <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                  Visit Count
                                </span>
                              </div>
                              <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                                {visit.visitCount || 0}
                              </span>
                            </div>

                            {/* Total Time */}
                            <div className="flex flex-col gap-2 min-w-[120px]">
                              <div className="flex items-center gap-2">
                                <Icon
                                  name="duration"
                                  className="w-4 h-4 text-[#2a2a2a]"
                                />
                                <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                  Total Time
                                </span>
                              </div>
                              <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                                {formatDuration(visit.totalTime || 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-[50px]">
                      <p className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#666] mb-4">
                        No page visits recorded
                      </p>
                      <p className="font-['Axiforma:Regular',_sans-serif] text-[14px] text-[#999]">
                        Page visit data will appear here once the visitor
                        browses your website.
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Activity Section */}
              <section id="activity" className="mb-[50px]">
                <div className="flex items-center gap-2.5 mb-[30px]">
                  <Icon
                    name="drawer_activity"
                    className="w-5 h-5 text-[#000000]"
                  />
                  <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                    Activity
                  </h2>
                </div>

                <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                  <div className="text-center py-[50px]">
                    <p className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#666] mb-4">
                      Activity tracking coming soon...
                    </p>
                    <p className="font-['Axiforma:Regular',_sans-serif] text-[14px] text-[#999]">
                      This section will show visitor activity, page views, and
                      engagement metrics.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </Drawer>
      );
    };
  }

  // Toggle state for grouped buttons
  // const [activeTab, setActiveTab] = React.useState("personal");

  return (
    <div className="flex flex-col w-full h-screen relative max-w-full">
      {/* Header Section - Fixed at top */}
      <div className="sticky  top-0 z-10 bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between gap-x-16 gap-y-2 flex-wrap-reverse">
          {/* Displaying Leads Text - Dynamic based on pagination */}
          <div className="flex">
            <p className="text-[14px] font-medium text-[#2a2a2a] whitespace-nowrap">
              {totalItems > 0
                ? `Displaying Leads ${
                    startIndex + 1
                  } - ${endIndex} of ${totalItems} In Total`
                : "No leads found"}
            </p>
          </div>

          {/* Grouped Toggle Buttons */}
          <div className="flex flex-1 bg-[#e9e9e9] rounded-[30px]  shadow-sm gap-1">
            {/* Person Level ID Button */}
            <button
              type="button"
              aria-pressed={activeTab === "personal"}
              onClick={() => {
                setActiveTab("personal");
                navigate({
                  pathname: "/person-level-id",
                  search: location.search,
                });
              }}
              className={`relative h-[50px] w-full rounded-[30px] flex justify-center items-center px-4 py-3.5 font-bold text-[14px] tracking-[-0.154px] focus:outline-none transition-all duration-300 ease-in-out overflow-hidden group
                ${
                  activeTab === "personal"
                    ? "bg-gradient-to-r from-[#5e78f4] to-[#f14ce2] text-white shadow-lg scale-[1.02] hover:shadow-xl hover:from-[#6b85f5] hover:to-[#f45be9]"
                    : "bg-[#e9e9e9] text-[#2a2a2a] hover:bg-[#ddd] hover:scale-[1.01] hover:shadow-md"
                }
              `}
            >
              {/* Subtle shine effect for active state */}
              {activeTab === "personal" && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out"></div>
              )}
              <span className="relative transition-all duration-200 group-hover:translate-x-0.5">
                Person Level Identity
              </span>
            </button>

            {/* Company Level ID Button */}
            <button
              type="button"
              aria-pressed={activeTab === "company"}
              onClick={() => {
                setActiveTab("company");
                navigate({
                  pathname: "/company-level-id",
                  search: location.search,
                });
              }}
              className={`relative h-[50px] w-full rounded-[30px] flex justify-center items-center px-4 py-3.5 font-bold text-[14px] tracking-[-0.154px] focus:outline-none transition-all duration-300 ease-in-out overflow-hidden group
                ${
                  activeTab === "company"
                    ? "bg-gradient-to-r from-[#5e78f4] to-[#f14ce2] text-white shadow-lg scale-[1.02] hover:shadow-xl hover:from-[#6b85f5] hover:to-[#f45be9]"
                    : "bg-[#e9e9e9] text-[#2a2a2a] hover:bg-[#ddd] hover:scale-[1.01] hover:shadow-md"
                }
              `}
            >
              {/* Subtle shine effect for active state */}
              {activeTab === "company" && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out"></div>
              )}
              <span className="relative transition-all duration-200 group-hover:translate-x-0.5">
                Company level Identity
              </span>
            </button>
          </div>

          {/* export to excel button with svg icon */}
          {getPlanDetails(true) == 6 ? (
            <Tooltip
              content="Please upgrade to export leads"
              trigger="hover"
              style="light"
              placement="top"
        >
            <button
              type="default"
              onClick={() => setIsUpgradeModalOpen(true)}
              className="py-2 px-4 max-w-fit w-full rounded-full  ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75 flex align-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:ring-[1px]"
            >
              Export to Excel
            </button>
            </Tooltip>
          ) : (
            <button
              type="default"
              onClick={exportToExcel}
              disabled={isExporting}
              className="py-2 px-4 max-w-fit w-full rounded-full  ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75 flex align-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:ring-[1px]"
            >
              {isExporting ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                "Export to Excel"
              )}
            </button>
          )}
        </div>
      </div>

      {/* Leads List - Scrollable content area */}
      <div className="flex-1 overflow-auto relative">
        {leadsLoading ? (
          // Skeleton Loader - matches LeadCard structure
          <>
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="relative flex items-center gap-5 px-4 py-4 bg-[#FAFBFC] border-b border-[#DCDCDC] flex-wrap"
              >
                {/* Skeleton Avatar */}
                <div className="w-[110px] h-[110px] rounded-full bg-gray-300 skeleton-wave shrink-0"></div>

                {/* Skeleton Information Sections */}
                <div className="flex-1 flex gap-y-3 gap-x-2 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4 min-w-0">
                  {/* Name Section Skeleton */}
                  <div className="flex gap-2.5 divide-x-2 [&>*:not(:first-child)]:pl-2 [&>*]:pr-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-3.5 h-3 bg-gray-300 rounded skeleton-wave shrink-0"></div>
                        <div className="w-12 h-3 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      <div className="w-32 h-4 bg-gray-300 rounded skeleton-wave mt-1"></div>
                    </div>
                    <div className="flex items-center pt-3 gap-2.5 shrink-0">
                      <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                  </div>

                  {/* ICP Score Skeleton */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-3.5 h-3 bg-gray-300 rounded skeleton-wave shrink-0"></div>
                      <div className="w-16 h-3 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                    <div className="w-12 h-5 bg-gray-300 rounded skeleton-wave"></div>
                  </div>

                  {/* Title Skeleton */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-[13px] h-3 bg-gray-300 rounded skeleton-wave shrink-0"></div>
                      <div className="w-10 h-3 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                    <div className="w-40 h-4 bg-gray-300 rounded skeleton-wave max-w-full"></div>
                  </div>

                  {/* Company Name Skeleton */}
                  <div className="flex gap-2.5 divide-x-2 [&>*:not(:first-child)]:pl-2 [&>*]:pr-2 min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-3 h-3 bg-gray-300 rounded skeleton-wave shrink-0"></div>
                        <div className="w-24 h-3 bg-gray-300 rounded skeleton-wave"></div>
                      </div>
                      <div className="w-36 h-4 bg-gray-300 rounded skeleton-wave max-w-full"></div>
                    </div>
                    <div className="flex items-center pt-3 gap-2.5 shrink-0">
                      <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="w-4 h-4 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                  </div>

                  {/* Last Visit Skeleton */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-3 h-3 bg-gray-300 rounded skeleton-wave shrink-0"></div>
                      <div className="w-20 h-3 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                    <div className="w-48 h-4 bg-gray-300 rounded skeleton-wave max-w-full"></div>
                  </div>

                  {/* Duration Skeleton */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-[11px] h-3 bg-gray-300 rounded skeleton-wave shrink-0"></div>
                      <div className="w-16 h-3 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                    <div className="w-24 h-4 bg-gray-300 rounded skeleton-wave"></div>
                  </div>
                </div>

                {/* Skeleton Action Button */}
                <div className="w-[200px] flex flex-col gap-5 shrink-0">
                  <div className="w-full h-11 bg-gray-300 rounded-full skeleton-wave"></div>
                </div>
              </div>
            ))}
          </>
        ) : allLeads.length === 0 ? (
          <NoLeadsComponent />
        ) : (
          allLeads.map((lead, index) => (
            <LeadCard key={lead._id} lead={lead} index={index} />
          ))
        )}
      </div>

      {/* Drawer for Lead Details */}

      {/* Pagination - Floating at bottom */}
      {!leadsLoading && allLeads.length > 0 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <Pagination />
        </div>
      )}
      {/* Details Drawer - For viewing lead details */}
      <DetailsDrawer
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        drawerData={drawerData}
        setDrawerData={setDrawerData}
        drawerLoading={false}
        activeTab={activeTab}
        setIsUpgradeModalOpen={setIsUpgradeModalOpen}
      />
      <SuccessModal
        open={openSuccessModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
        from={"MainContent"}
      />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />

      {/* Thumbs Down Feedback Modal */}
      <Modal
        show={ratingModalOpen}
        onClose={() => {
          setRatingModalOpen(false);
          setRatingComment("");
          setRatingLeadId(null);
        }}
        size="md"
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <form onSubmit={(e) => {
            e.preventDefault();
            submitLeadRating(ratingLeadId, 0, ratingComment);
          }} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Why this data is inaccurate?
            </h3>
            <p className="text-sm text-gray-500">
              Help us improve by sharing your feedback.<span className="text-red-500">*</span>
            </p>
            <textarea
              required
              className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-[#5e78f4] focus:border-[#5e78f4] resize-none"
              placeholder="Please share why this data is inaccurate..."
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setRatingModalOpen(false);
                  setRatingComment("");
                  setRatingLeadId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                // onClick={() => submitLeadRating(ratingLeadId, 0, ratingComment)}
                disabled={ratingSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#5e78f4] to-[#f14ce2] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {ratingSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MainContent;
